/**
 * promptDiff — line diff between two prompt revisions.
 *
 * Thin, typed wrapper over jsdiff so the component renders a flat row list
 * and the interesting logic (splitting hunks into lines, dropping the
 * trailing empty line jsdiff emits for a newline-terminated value) stays a
 * pure function the node-env vitest config can actually test.
 */

import { diffLines } from 'diff'

export type DiffKind = 'added' | 'removed' | 'unchanged'

export interface DiffRow {
  kind: DiffKind
  text: string
}

/** One row per line, in order, tagged with how it changed. */
export function diffPromptLines(before: string, after: string): DiffRow[] {
  const rows: DiffRow[] = []
  for (const part of diffLines(before, after)) {
    const kind: DiffKind = part.added ? 'added' : part.removed ? 'removed' : 'unchanged'
    const lines = part.value.split('\n')
    // A hunk ending in a newline splits into a final '' that is not a line.
    if (lines.length > 1 && lines[lines.length - 1] === '') {
      lines.pop()
    }
    for (const text of lines) {
      rows.push({ kind, text })
    }
  }
  return rows
}

/** Counts for a one-line "+N −M" summary. */
export function diffStat(rows: DiffRow[]): { added: number; removed: number } {
  return {
    added: rows.filter((r) => r.kind === 'added').length,
    removed: rows.filter((r) => r.kind === 'removed').length,
  }
}
